import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
// Route screens are lazy-loaded so each route fetches only its own code.
// The landing page stays light; heavy screens (game, squads, matches) load on demand.
const LandingPage = lazy(() => import('@/modules/ui/screens/Landing/LandingPage.jsx'));
const GameSetup = lazy(() => import('@/modules/game/ui/GameSetup/index.jsx'));
const OpponentSetup = lazy(() => import('@/modules/game/ui/OpponentSetup.jsx'));
const GameManagementScreen = lazy(() => import('@/modules/game/ui/GameManagement/index.jsx'));
const GameSummaryScreen = lazy(() => import('@/modules/game/ui/GameSummaryScreen.jsx'));
import { LoadingState, NavBar, PWAInstallBanner, PasskeyPromptBanner, VersionChecker } from '@/modules/ui/components';
const AuthenticateScreen = lazy(() => import('@/modules/auth/ui/AuthenticateScreen.jsx'));
const AuthScreen = lazy(() => import('@/modules/auth/ui/AuthScreen.jsx'));
const SquadsScreen = lazy(() => import('@/modules/squads/ui/SquadsScreen.jsx'));
const SquadPlayersScreen = lazy(() => import('@/modules/squads/ui/SquadPlayersScreen.jsx'));
const SquadOptionsScreen = lazy(() => import('@/modules/squads/ui/SquadOptionsScreen.jsx'));
const SquadSelectForMatchScreen = lazy(() => import('@/modules/squads/ui/SquadSelectForMatchScreen.jsx'));
const SeasonStatsScreen = lazy(() => import('@/modules/squads/ui/SeasonStatsScreen.jsx'));
const AdminScreen = lazy(() => import('@/modules/admin/ui/AdminScreen.jsx'));
const UnsubscribeScreen = lazy(() => import('@/modules/ui/screens/UnsubscribeScreen.jsx'));
const MatchHistoryScreen = lazy(() => import('@/modules/matches/ui/MatchHistoryScreen.jsx'));
const MatchDetailScreen = lazy(() => import('@/modules/matches/ui/MatchDetailScreen.jsx'));
const AccountScreen = lazy(() => import('@/modules/ui/screens/AccountScreen.jsx'));
import OpponentNameModal from '@/modules/game/ui/OpponentNameModal';
import TrialBanner from '@/modules/subscriptions/ui/TrialBanner';
import { ProtectedRoute, PublicRoute, useAuthContext } from '@/modules/auth/context/AuthProvider';
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription';
import { useMatchDataManager } from '@/modules/game/hooks/useMatchDataManager';
import { 
  safeParseLocalStorage, 
  validatePendingMatchData, 
  attemptMatchDataRepair,
  cleanupCorruptedLocalStorage 
} from '@/modules/core/utils';
import * as Sentry from '@sentry/browser';

// Component to handle subscription cancellation redirect
function SubscriptionCancelRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Subscription canceled, redirecting user to appropriate location');
    
    // Log cancellation event for analytics
    try {
      Sentry.addBreadcrumb({
        message: 'User canceled subscription upgrade, redirecting to previous location',
        level: 'info',
        category: 'subscription'
      });
    } catch (error) {
      // Ignore Sentry errors
    }

    // Check if user was in the middle of a game
    const pendingMatchData = localStorage.getItem('pending_match_data');
    if (pendingMatchData) {
      // Take them back to the game summary
      console.log('Returning cancelled user to match summary');
      navigate('/summary');
    } else {
      // Take them back to squads
      console.log('Returning cancelled user to squads');
      navigate('/squads');
    }
  }, [navigate]);

  // Show nothing while redirecting
  return null;
}

// Component to handle subscription success redirect
function SubscriptionSuccessRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Subscription success detected, checking for pending match data');
    
    // Log success event for analytics
    try {
      Sentry.addBreadcrumb({
        message: 'User completed subscription upgrade, checking for pending match data',
        level: 'info',
        category: 'subscription'
      });
    } catch (error) {
      // Ignore Sentry errors
    }

    // Set a flag to indicate successful upgrade
    localStorage.setItem('subscription_just_upgraded', 'true');
    
    // Check if user has pending match data
    const pendingMatchData = localStorage.getItem('pending_match_data');
    if (pendingMatchData) {
      console.log('Found pending match data, redirecting to summary to complete save');
      // Redirect to summary where the match save logic will handle pending data
      navigate('/summary');
    } else {
      console.log('No pending match data, redirecting to squads');
      // No pending match, redirect to squads
      navigate('/squads');
    }
  }, [navigate]);

  // Show loading while redirecting
  return <LoadingState fullPage message="Welcome to Premium! Redirecting..." />;
}

function App() {
  const { user } = useAuthContext();
  const { isPremium } = useSubscription();
  const { saveMatchData } = useMatchDataManager();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for handling pending match save
  const [showOpponentModal, setShowOpponentModal] = useState(false);
  const [pendingMatchData, setPendingMatchData] = useState(null);
  const [saveStatus, setSaveStatus] = useState({
    inProgress: false,
    attempted: false
  });

  // Clean up corrupted localStorage on app start
  useEffect(() => {
    try {
      cleanupCorruptedLocalStorage();
    } catch (error) {
      console.error('Error during localStorage cleanup:', error);
      Sentry.captureException(error);
    }
  }, []);

  // Handle manual match save
  const handleManualSave = async (opponentName) => {
    try {
      if (!pendingMatchData) {
        throw new Error('No match data available to save');
      }

      // Re-validate the data before saving
      const validation = validatePendingMatchData(pendingMatchData);
      if (!validation.isValid) {
        throw new Error(`Invalid match data structure: ${validation.error}`);
      }

      console.log('Manually saving match data after upgrade:', { 
        opponentName,
        gameStateKeys: Object.keys(pendingMatchData.gameState),
        playerCount: pendingMatchData.playerData.length
      });
      setSaveStatus(prev => ({ ...prev, inProgress: true }));
      
      // Store opponent name
      localStorage.setItem('opponent_name', opponentName);
      
      // Save the match data using the stored data
      await saveMatchData(pendingMatchData.gameState, pendingMatchData.playerData);
      
      // Clear pending match data
      localStorage.removeItem('pending_match_data');
      localStorage.setItem('match_saved', 'true');
      
      setShowOpponentModal(false);
      setPendingMatchData(null);
      setSaveStatus(prev => ({ ...prev, inProgress: false }));
      
      console.log('Match saved successfully!');
      
    } catch (error) {
      console.error('Error saving match after upgrade:', error);
      Sentry.captureException(error, {
        extra: {
          opponentName,
          hasMatchData: !!pendingMatchData,
          userId: user?.id,
          matchDataStructure: pendingMatchData ? {
            gameStateKeys: Object.keys(pendingMatchData.gameState || {}),
            playerDataLength: pendingMatchData.playerData?.length || 0
          } : null,
          context: 'handleManualSave'
        }
      });
      setSaveStatus(prev => ({ ...prev, inProgress: false }));
      console.error('Failed to save match data. Please try again.');
    }
  };

  // Handle skipping match save
  const handleSkipSave = () => {
    setShowOpponentModal(false);
    setPendingMatchData(null);
    localStorage.removeItem('pending_match_data');
    localStorage.setItem('match_saved', 'skip');
    console.log('User chose to skip saving match data');
    console.log('Match data was not saved');
  };

  // Check if we should hide the header (on manage screen)
  const hideHeader = location.pathname === '/manage';

  return (
    <>
      {!hideHeader && <NavBar />}
      {!hideHeader && <TrialBanner />}
      {!hideHeader && <PWAInstallBanner />}
      {!hideHeader && <PasskeyPromptBanner />}
      <VersionChecker />
      <Suspense fallback={<LoadingState fullPage message="Loading..." />}>
      <Routes>
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><AuthScreen /></PublicRoute>} />
        <Route path="/authenticate" element={<PublicRoute><AuthenticateScreen /></PublicRoute>} />
        <Route path="/subscription/success" element={
          <ProtectedRoute>
            <SubscriptionSuccessRedirect />
          </ProtectedRoute>
        } />
        <Route path="/subscription/cancel" element={
          <ProtectedRoute>
            <SubscriptionCancelRedirect />
          </ProtectedRoute>
        } />
        <Route path="/squads" element={
          <ProtectedRoute>
            <SquadsScreen />
          </ProtectedRoute>
        } />
        <Route path="/squads/:squadId/options" element={
          <ProtectedRoute>
            <SquadOptionsScreen />
          </ProtectedRoute>
        } />
        <Route path="/squads/:squadId/players" element={
          <ProtectedRoute>
            <SquadPlayersScreen />
          </ProtectedRoute>
        } />
        <Route path="/squads/:squadId/select-for-match" element={
          <ProtectedRoute>
            <SquadSelectForMatchScreen />
          </ProtectedRoute>
        } />
        <Route path="/squads/:squadId/stats" element={
          <ProtectedRoute>
            <SeasonStatsScreen />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminScreen />
          </ProtectedRoute>
        } />
        <Route path="/unsubscribe" element={<UnsubscribeScreen />} />
        <Route path="/matches" element={
          <ProtectedRoute>
            <MatchHistoryScreen />
          </ProtectedRoute>
        } />
        <Route path="/matches/:matchId" element={
          <ProtectedRoute>
            <MatchDetailScreen />
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute>
            <AccountScreen />
          </ProtectedRoute>
        } />
        <Route path="/setup" element={
          <ProtectedRoute>
            <GameSetup />
          </ProtectedRoute>
        } />
        <Route path="/opponent-setup" element={
          <ProtectedRoute>
            <OpponentSetup />
          </ProtectedRoute>
        } />
        <Route path="/manage" element={
          <ProtectedRoute>
            <GameManagementScreen />
          </ProtectedRoute>
        } />
        <Route path="/summary" element={
          <ProtectedRoute>
            <GameSummaryScreen />
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>
      
      {/* Opponent Name Modal for manual match save */}
      <OpponentNameModal
        isOpen={showOpponentModal}
        onClose={handleSkipSave}
        onConfirm={handleManualSave}
      />
    </>
  );
}

export default App;